import {mark, types as metrics} from '@servicenow/ui-metrics';
import log from '../../log';
import * as operations from '../operators/operations';
import {splitPath} from '@servicenow/ui-internal';
import {schedule, types} from '../../scheduler';
import {properties as parseProperties} from '../parse';
import getHostVariables from '../getHostVariables';
import getStatePropsContext from './getStatePropsContext';
import deferHelperExecution from './deferHelperExecution';
import {HELPERS_SCHEDULED as HELPERS_SCHEDULED_CONST, HELPERS_DEFER} from '../../constants';
import {HELPERS_SCHEDULED, SET_PROPERTY, PROPS} from '../../symbols';
import quickEquals from './quickEquals';

const {IMMEDIATE, PROPERTY_SET} = types;
const {
	declarativeOperations: {ASSIGN}
} = operations;

const setProperties = (host, properties, propertyDefs, interactionId) => {
	const hostVariables = getHostVariables(host);
	const {dispatch} = hostVariables[HELPERS_SCHEDULED];
	const parsedProperties = parseProperties(properties, propertyDefs, {host, dispatch});
	for (const k in parsedProperties) {
		// for backwards compatibility an undefined must still be set on the instance
		if (!propertyDefs.hasOwnProperty(k)) {
			host[k] = parsedProperties[k]; // eslint-disable-line no-param-reassign
			if (process.env.NODE_ENV === 'development') {
				log(
					`${host.tagName.toLowerCase()} is missing a property definition for "${k}".  Properties set with missing definitions will not be available to the view or action handlers.`,
					{host, origin: 'setProperties'}
				);
			}
		} else {
			// FIXME: Not handling cases where component starts with no old props
			let old = hostVariables[PROPS][k];
			let current = parsedProperties[k];
			if (quickEquals(old, current)) {
				continue;
			}

			host[SET_PROPERTY](k, parsedProperties[k], true, interactionId); // eslint-disable-line no-param-reassign
		}
	}
};

export default function createUpdateProperties(host, propertyDefs, executionTiming) {
	return function updateProperties(update, interactionId) {
		if (executionTiming === HELPERS_DEFER)
			mark(host, interactionId, metrics.DEFER, {deferred: metrics.UPDATE_PROPERTIES});
		const meta = {el: host, interactionId};
		deferHelperExecution(
			executionTiming,
			() => {
				schedule({
					fn() {
						mark(host, interactionId, metrics.UPDATE_PROPERTIES, {
							update: typeof update === 'function' ? 'DECLARATIVE' : 'OBJECT'
						});
						if (typeof update === 'function') {
							const stateProps = getStatePropsContext(host, interactionId);
							const {
								shouldUpdate = true,
								operation = ASSIGN,
								path,
								value,
								start,
								deleteCount,
								addItems
							} = update(stateProps);

							if (shouldUpdate) {
								try {
									const propertyPath = splitPath(path);
									const propertyLen = propertyPath.length;
									const [name] = propertyPath;
									const propertyValue = host[name];
									if (propertyValue === null || typeof propertyValue !== 'object') {
										setProperties(host, {[name]: value}, propertyDefs, interactionId);
									} else {
										const propertyValue = {};
										let pvRef = propertyValue;
										let hostRef = host;
										for (let i = 0; i < propertyLen; i++) {
											const name = propertyPath[i];
											const value = hostRef[name];
											if (value === null || typeof value !== 'object') break;
											if (Array.isArray(value)) pvRef[name] = value.slice();
											else pvRef[name] = {...value};

											pvRef = pvRef[name];
											hostRef = hostRef[name];
										}
										operations[operation](propertyValue, {
											path: propertyLen ? propertyPath : [],
											value,
											start,
											deleteCount,
											addItems: Array.isArray(addItems) ? addItems : []
										});
										setProperties(host, propertyValue, propertyDefs, interactionId);
									}
								} catch (error) {
									log('Error while performing properties update', {
										error,
										host,
										origin: 'updateProperties'
									});
								}
							}
						} else {
							setProperties(host, update, propertyDefs, interactionId);
						}
					},
					type: executionTiming !== HELPERS_SCHEDULED_CONST ? IMMEDIATE : PROPERTY_SET,
					meta
				});
			},
			meta
		);
	};
}
